import { useCallback, useMemo, useState } from 'react'
import { BotAuthorizationDisplay } from '@components/BotAuthorizationDisplay/BotAuthorizationDisplay'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { Box, Button, FancyButton, Icon, Paragraph, Stack, Text } from '@ui'
import { type GetSigner, WalletReady } from 'privy/WalletReady'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import {
    SignerUndefinedError,
    TransactionStatus,
    useBaseWrites,
    useBotData,
    useBotStatus,
    useInstallAppOnUserAccountTransaction,
    useInstallAppWithModuleTransaction,
} from 'use-towns-client'
import { type Address, formatEther } from 'viem'

const BOT_FRAMEWORK_MINIMUM_VERSION_FOR_BOT_DM = { major: 0, minor: 0, patch: 454 }

type ParsedVersion = { major: number; minor: number; patch: number } | null

const parseBotVersion = (version: string | undefined): ParsedVersion => {
    if (!version) {
        return null
    }
    const match = version.match(/javascript:@towns-protocol\/bot:(\d+)\.(\d+)\.(\d+)$/)
    if (!match) {
        return null
    }
    return {
        major: parseInt(match[1], 10),
        minor: parseInt(match[2], 10),
        patch: parseInt(match[3], 10),
    }
}

const isVersionSupported = (version: ParsedVersion): boolean => {
    if (!version) {
        return false
    }
    if (version.major !== BOT_FRAMEWORK_MINIMUM_VERSION_FOR_BOT_DM.major) {
        return version.major > BOT_FRAMEWORK_MINIMUM_VERSION_FOR_BOT_DM.major
    }
    if (version.minor !== BOT_FRAMEWORK_MINIMUM_VERSION_FOR_BOT_DM.minor) {
        return version.minor > BOT_FRAMEWORK_MINIMUM_VERSION_FOR_BOT_DM.minor
    }
    return version.patch >= BOT_FRAMEWORK_MINIMUM_VERSION_FOR_BOT_DM.patch
}

export interface BotInstallContentProps {
    appAddress: Address
    walletAddress: Address
    onSuccess?: () => void
    /** When true, shows cancel button and modal-style padding */
    isCancellable?: boolean
    onCancel?: () => void
    cancelLabel?: string
    /** When true, suppresses the success toast (useful when parent will show its own toast) */
    suppressSuccessToast?: boolean
    /** External loading state (e.g., when parent is performing additional operations after install) */
    isExternalLoading?: boolean
    /** When true, shows bot description instead of permissions (for DM install flow) */
    isDMInstall?: boolean
}

type InstallBlockReason = 'deactivated' | 'not-responding' | 'version-outdated' | null

export const BotInstallContent = ({
    appAddress,
    walletAddress,
    onSuccess,
    isCancellable = false,
    onCancel,
    cancelLabel = 'Cancel',
    suppressSuccessToast = false,
    isExternalLoading = false,
    isDMInstall = false,
}: BotInstallContentProps) => {
    const [isInstalling, setIsInstalling] = useState(false)

    const { userOpsInstance } = useBaseWrites()
    const installAppOnUserAccount = useInstallAppOnUserAccountTransaction()
    const installAppWithModule = useInstallAppWithModuleTransaction()

    // Fetch bot data using the appAddress
    const { data: bot, isLoading: isBotDataLoading } = useBotData(appAddress)

    // Fetch bot status for version check
    const { data: botStatus, isLoading: isBotStatusLoading } = useBotStatus(bot?.clientAddress)

    const installBlockReason: InstallBlockReason = useMemo(() => {
        if (isBotStatusLoading) {
            return null
        }
        const parsedVersion = parseBotVersion(botStatus?.version)
        return (() => {
            if (botStatus?.isActive === false) {
                return 'deactivated'
            }
            if (botStatus?.isRegistered && !botStatus?.validResponse) {
                return 'not-responding'
            }
            if (!parsedVersion || !isVersionSupported(parsedVersion)) {
                return 'version-outdated'
            }
            return null
        })()
    }, [isBotStatusLoading, botStatus])

    const handleInstall = useCallback(
        async (getSigner: GetSigner) => {
            if (!bot) {
                return
            }
            const signer = await getSigner()
            if (!signer) {
                throw new SignerUndefinedError()
            }
            setIsInstalling(true)
            try {
                if (!userOpsInstance) {
                    throw new Error('UserOps not available')
                }
                const hasAccountModules = await userOpsInstance.isAccountModulesInstalled(signer)
                if (hasAccountModules) {
                    // Always attempt installation - don't check isAppInstalledOnUserAccount first
                    // as client/server RPC nodes may be out of sync. If already installed,
                    // the contract will revert with AppManager__AppAlreadyInstalled (0x983ecc8e)
                    // which we treat as success.
                    const result = await installAppOnUserAccount.installAppOnUserAccount({
                        appAddress: bot.appAddress,
                        price: bot.installPrice,
                        signer,
                        walletAddress,
                    })
                    if (result?.status === TransactionStatus.Failed) {
                        // check if the error is "app already installed" - this is actually ok
                        // The error selector 0x983ecc8e = AppManager__AppAlreadyInstalled()
                        const errorString =
                            JSON.stringify(result.error) + (result.error?.message ?? '')
                        const isAlreadyInstalled = errorString.includes('0x983ecc8e')
                        if (!isAlreadyInstalled) {
                            throw result.error ?? new Error('Failed to install app')
                        }
                    }
                } else {
                    const result = await installAppWithModule.installAppWithModule({
                        appAddress: bot.appAddress,
                        price: bot.installPrice,
                        signer,
                        walletAddress,
                    })
                    if (result?.status === TransactionStatus.Failed) {
                        throw result.error ?? new Error('Failed to install AccountModules and app')
                    }
                }

                if (!suppressSuccessToast) {
                    popupToast(({ toast }) => (
                        <StandardToast.Success
                            toast={toast}
                            message="Bot installed successfully!"
                        />
                    ))
                }
                onSuccess?.()
            } catch (error) {
                console.error('[BotInstallContent] Bot installation failed:', error)
                popupToast(({ toast }) => (
                    <StandardToast.Error
                        toast={toast}
                        message="Failed to install bot"
                        subMessage="Please try again or contact support if the problem persists."
                    />
                ))
            } finally {
                setIsInstalling(false)
            }
        },
        [
            bot,
            installAppOnUserAccount,
            installAppWithModule,
            onSuccess,
            suppressSuccessToast,
            userOpsInstance,
            walletAddress,
        ],
    )

    const isPending =
        isInstalling ||
        installAppOnUserAccount.isLoading ||
        installAppWithModule.isLoading ||
        isExternalLoading ||
        isBotStatusLoading

    if (isBotDataLoading || isBotStatusLoading || !bot) {
        return (
            <Stack gap={isCancellable ? 'none' : 'md'}>
                <Box centerContent padding={isCancellable ? 'lg' : 'none'}>
                    <ButtonSpinner />
                </Box>
            </Stack>
        )
    }

    const priceLabel = bot.installPrice === 0n ? 'Free' : `${formatEther(bot.installPrice)} ETH`

    const isInstallDisabled = isPending || installBlockReason !== null

    return (
        <Stack gap={isCancellable ? 'none' : 'md'}>
            <Box padding={isCancellable ? 'lg' : 'none'}>
                {isDMInstall ? (
                    <Stack gap="lg" alignItems="center">
                        <Stack gap="md" alignItems="center">
                            <Box position="relative">
                                <Box
                                    as="img"
                                    src={bot?.metadata?.avatarUrl}
                                    width="x12"
                                    height="x12"
                                    aspectRatio="1/1"
                                    rounded="full"
                                    objectFit="cover"
                                />
                                <Box
                                    centerContent
                                    position="absolute"
                                    rounded="full"
                                    width="x4"
                                    height="x4"
                                    background="botGradient"
                                    bottom="none"
                                    right="none"
                                >
                                    <Icon type="robot" size="square_sm" />
                                </Box>
                            </Box>
                            <Text fontWeight="strong" fontSize="lg">
                                {bot?.metadata?.displayName || bot?.clientAddress}
                            </Text>
                        </Stack>
                        {bot?.metadata?.motto && (
                            <Box
                                padding="md"
                                background="level2"
                                rounded="sm"
                                width="100%"
                                borderLeft="strongFaint"
                            >
                                <Paragraph
                                    color="gray2"
                                    style={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}
                                >
                                    {`"${bot.metadata.motto}"`}
                                </Paragraph>
                            </Box>
                        )}
                        {bot?.metadata?.description && (
                            <Box padding="md" background="level2" rounded="sm" width="100%">
                                <Stack gap="md">
                                    <Text fontWeight="strong">Description</Text>
                                    <Paragraph color="gray2" style={{ whiteSpace: 'pre-wrap' }}>
                                        {bot.metadata.description}
                                    </Paragraph>
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                ) : (
                    <BotAuthorizationDisplay bot={bot} />
                )}
            </Box>

            {installBlockReason && (
                <Box padding={isCancellable ? 'lg' : 'none'}>
                    <Stack
                        horizontal
                        gap
                        padding="md"
                        background="level2"
                        rounded="md"
                        alignItems="center"
                    >
                        <Icon type="alert" size="square_sm" color="error" />
                        <Text color="gray2" fontSize="sm">
                            {(() => {
                                switch (installBlockReason) {
                                    case 'deactivated':
                                        return 'This bot has been deactivated.'
                                    case 'not-responding':
                                        return 'This bot is not responding.'
                                    case 'version-outdated':
                                        return 'This bot version does not support direct messages. Please contact the bot developer to update.'
                                }
                            })()}
                        </Text>
                    </Stack>
                </Box>
            )}

            <Box
                padding={isCancellable ? 'lg' : 'none'}
                paddingTop={isCancellable ? 'md' : 'none'}
                borderTop={isCancellable ? 'default' : undefined}
            >
                <Stack horizontal={isCancellable} justifyContent="end" gap="sm">
                    {isCancellable && onCancel && (
                        <Button
                            tone="level2"
                            size="button_md"
                            disabled={isPending}
                            onClick={onCancel}
                        >
                            {cancelLabel}
                        </Button>
                    )}
                    <WalletReady>
                        {({ getSigner }) => (
                            <FancyButton
                                cta={!isInstallDisabled}
                                disabled={isInstallDisabled}
                                spinner={isPending}
                                grow={!isCancellable}
                                onClick={() => handleInstall(getSigner)}
                            >
                                {isPending ? 'Installing...' : `Install for ${priceLabel}`}
                            </FancyButton>
                        )}
                    </WalletReady>
                </Stack>
            </Box>
        </Stack>
    )
}
